import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkles, Code, Plus, Check, Loader2 } from 'lucide-react';
import { Button, useAuth, kmsService, toast } from '@aaas/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useSpaceDetailQuery } from '../hooks/useSpaceListQuery';
import { useWidgetListQuery } from '../hooks';
import type { Widget } from '../types/widget.types';
import { AllWidgetsCardStack } from '../components/widget-association/AllWidgetsCardStack';
import { AssociatedWidgetsPanel } from '../components/widget-association/AssociatedWidgetsPanel';

const SpaceWidgetAssociationPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { idToken, user, currentWorkspace } = useAuth();
  const queryClient = useQueryClient();

  // Fetch space details
  const {
    data: space,
    isLoading: spaceLoading,
    error: spaceError,
  } = useSpaceDetailQuery({
    spaceId: id,
    idToken: idToken || undefined,
    enabled: !!id && !!user && !!currentWorkspace,
  });

  // Fetch all widgets (global, not space-filtered)
  const { data: widgetResponse, isLoading: widgetsLoading } = useWidgetListQuery({
    skip: 0,
    limit: 100,
    enabled: !!idToken,
  });

  // State
  const [currentIndex, setCurrentIndex] = useState(0);
  const [associatedWidgetIds, setAssociatedWidgetIds] = useState<Set<string>>(new Set());
  const [initialAssociatedWidgetIds, setInitialAssociatedWidgetIds] = useState<Set<string>>(
    new Set()
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Derived state
  const allWidgets = widgetResponse?.items ?? [];
  const currentWidget = allWidgets[currentIndex];
  const isCurrentWidgetAssociated = associatedWidgetIds.has(currentWidget?.id ?? '');
  const associatedWidgets = allWidgets.filter((w) => associatedWidgetIds.has(w.id));

  // Check if there are pending changes
  const hasPendingChanges =
    associatedWidgetIds.size !== initialAssociatedWidgetIds.size ||
    [...associatedWidgetIds].some((id) => !initialAssociatedWidgetIds.has(id)) ||
    [...initialAssociatedWidgetIds].some((id) => !associatedWidgetIds.has(id));

  // Initialize associated widget IDs from fetched data (only once, preserve pending changes)
  useEffect(() => {
    if (allWidgets.length > 0 && id && initialAssociatedWidgetIds.size === 0) {
      const associatedIds = new Set(
        allWidgets
          .filter((widget) => widget.spaces.some((s) => s.id === id))
          .map((widget) => widget.id)
      );
      setAssociatedWidgetIds(associatedIds);
      setInitialAssociatedWidgetIds(associatedIds);
    }
  }, [allWidgets, id, initialAssociatedWidgetIds.size]);

  // Add widget to space (local state only)
  const handleAddWidget = useCallback(() => {
    if (!currentWidget) return;

    // Add to local state
    setAssociatedWidgetIds((prev) => new Set([...prev, currentWidget.id]));

    // Move to next widget
    setCurrentIndex((prev) => prev + 1);
  }, [currentWidget]);

  // Remove widget from space (local state only)
  const handleRemoveWidget = useCallback((widget: Widget) => {
    // Remove from local state
    setAssociatedWidgetIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(widget.id);
      return newSet;
    });
  }, []);

  // Skip to next widget
  const handleSkipWidget = useCallback(() => {
    setCurrentIndex((prev) => prev + 1);
  }, []);

  // Review again - reset to first widget
  const handleReviewAgain = useCallback(() => {
    setCurrentIndex(0);
  }, []);

  // Submit all changes (batch update)
  const handleSubmit = useCallback(async () => {
    if (!id || !idToken || !hasPendingChanges) return;

    setIsSubmitting(true);

    try {
      // Batch update: replace all widget associations for this space
      const widgetIds = [...associatedWidgetIds];
      await kmsService.updateSpaceWidgets(idToken, id, widgetIds);

      // Invalidate queries to refetch fresh data
      queryClient.invalidateQueries({ queryKey: ['kms-widgets'] });

      // Update initial state to match current state
      setInitialAssociatedWidgetIds(new Set(associatedWidgetIds));

      // Show success message
      const changeCount =
        Math.abs(associatedWidgetIds.size - initialAssociatedWidgetIds.size) ||
        [...associatedWidgetIds].filter((wid) => !initialAssociatedWidgetIds.has(wid)).length +
          [...initialAssociatedWidgetIds].filter((wid) => !associatedWidgetIds.has(wid)).length;

      toast.success(`Successfully updated ${changeCount} widget association(s)`);
    } catch (error) {
      // Show error notification
      const errorMessage =
        error && typeof error === 'object' && 'message' in error
          ? (error as { message: string }).message
          : 'Unknown error';
      toast.error(`Failed to save changes: ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    id,
    idToken,
    hasPendingChanges,
    associatedWidgetIds,
    initialAssociatedWidgetIds,
    queryClient,
  ]);

  // Click widget from associated panel
  const handleWidgetClick = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' && !isCurrentWidgetAssociated) {
        handleAddWidget();
      } else if (e.key === 'ArrowLeft') {
        handleSkipWidget();
      } else if (e.key === 'Escape') {
        navigate(`/kms/spaces/${id}`);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleAddWidget, handleSkipWidget, id, navigate, isCurrentWidgetAssociated]);

  // Loading state
  if (spaceLoading || widgetsLoading) {
    return (
      <div className="w-full min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  // Error state
  if (spaceError || !space) {
    return (
      <div className="w-full min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-gray-600">Failed to load space details</p>
          <Button onClick={() => navigate(`/kms/spaces/${id}`)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  // No widgets in workspace
  if (allWidgets.length === 0) {
    return (
      <div className="w-full min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate(`/kms/spaces/${id}`)}
              className="p-2"
              aria-label="Go back"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                <Sparkles className="w-8 h-8 text-purple-600" />
                Manage Widget Associations
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                for <span className="font-semibold">{space.name}</span>
              </p>
            </div>
          </div>

          {/* Empty state */}
          <div className="flex items-center justify-center min-h-[500px]">
            <div className="text-center space-y-6 max-w-md">
              <Sparkles className="w-20 h-20 text-purple-300 mx-auto" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">No Widgets Found</h2>
                <p className="text-gray-600">
                  Create a widget first to associate it with this space.
                </p>
              </div>
              <Button
                onClick={() => navigate('/kms/widgets/create')}
                className="!bg-purple-600 hover:!bg-purple-700 !text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Widget
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate(`/kms/spaces/${id}`)}
              className="p-2 hover:bg-white/50"
              aria-label="Go back"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Widget Associations</h1>
              <p className="text-sm text-gray-600 mt-1">
                {space.name}
                {associatedWidgets.length > 0 && (
                  <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2.5 py-0.5 rounded-full font-medium">
                    {associatedWidgets.length}
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* View Embed Codes Button */}
          {associatedWidgets.length > 0 && !hasPendingChanges && (
            <Button
              onClick={() => navigate(`/kms/spaces/${id}/widgets-embed`)}
              className="!bg-purple-600 hover:!bg-purple-700 !text-white"
            >
              <Code className="w-4 h-4 mr-2" />
              Embed Codes
            </Button>
          )}
        </div>

        {/* Main Content */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left Panel: Widget Preview */}
          <div className="flex-1 flex items-center justify-center h-[700px]">
            <AllWidgetsCardStack
              widgets={allWidgets}
              currentIndex={currentIndex}
              isCurrentAssociated={isCurrentWidgetAssociated}
              onAdd={handleAddWidget}
              onSkip={handleSkipWidget}
              onReviewAgain={handleReviewAgain}
              isLoading={false}
            />
          </div>

          {/* Right Panel: Associated Widgets */}
          <div className="flex-1 lg:max-w-md flex-shrink-0 relative">
            <div className="h-full">
              <AssociatedWidgetsPanel
                associatedWidgets={associatedWidgets}
                allWidgets={allWidgets}
                currentIndex={currentIndex}
                onWidgetClick={handleWidgetClick}
                onRemove={handleRemoveWidget}
                isRemoving={false}
              />
            </div>

            {/* Submit Changes Button - Positioned absolutely below */}
            {hasPendingChanges && (
              <div className="absolute -bottom-20 left-0 right-0 flex justify-center mt-4">
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  size="lg"
                  className="rounded-full px-8 py-6 text-base font-bold !bg-green-600 hover:!bg-green-700 !text-white shadow-lg hover:shadow-xl transition-all"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="w-5 h-5" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpaceWidgetAssociationPage;
